import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { UntypedFormGroup, UntypedFormBuilder, Validators } from '@angular/forms';
import { finalize } from 'rxjs/operators';

import { MatSnackBar } from '@angular/material/snack-bar';

import { environment } from '@env/environment';
import { Logger } from '@core';
import { AuthDataService } from '../auth-data.service';

import { JournalService, MailService, Mail } from '@core';
import { CookieService } from 'ngx-cookie-service';

const log = new Logger('Login');

@Component({
  selector: 'app-login',
  templateUrl: './login-page.component.html',
  styleUrls: ['./login-page.component.scss'],
})
export class LoginPageComponent implements OnInit, OnDestroy {
  version: string | null = environment.version;
  error: string | undefined;
  loginForm!: UntypedFormGroup;
  isLoading = false;
  eye: boolean = false;
  eyeIcon: string = 'visibility_off';

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private formBuilder: UntypedFormBuilder,
    private authService: AuthDataService,
    private _snackBar: MatSnackBar,
    private mailService: MailService,
    private journalService: JournalService,
    private cookieService: CookieService
  ) {
    this.createForm();
  }

  ngOnInit() {
    // Check for error messages from query params (e.g., from activation failures)
    this.route.queryParams.subscribe((params) => {
      const error = params['error'];
      const message = params['message'];
      
      if (error && message) {
        this._snackBar.open(message, 'Schließen', {
          horizontalPosition: 'center',
          verticalPosition: 'top',
          duration: 5000,
        });
      }
      
      // Check for token and account_id query parameters (backdoor login from webapp)
      const token = params['token'];
      const accountIdParam = params['account_id'];
      
      if (token && accountIdParam) {
        // Parse accountId as number (query params are strings)
        const accountId = parseInt(accountIdParam, 10);
        if (!isNaN(accountId)) {
          // Auto-login with token
          this.autoLoginWithToken(token, accountId);
        } else {
          console.error('Invalid account_id parameter:', accountIdParam);
          this._snackBar.open('Ungültiger Account-ID Parameter', 'Schließen', {
            horizontalPosition: 'center',
            verticalPosition: 'top',
            duration: 3000,
          });
        }
      }
    });
  }

  autoLoginWithToken(token: string, accountId: number) {
    this.isLoading = true;
    
    const login$ = this.authService.loginWithToken(token, accountId);
    login$
      .pipe(
        finalize(() => {
          this.isLoading = false;
        })
      )
      .subscribe(
        (data) => {
          // Check if user is kontakt (role_id 12)
          const userRole = data.role;
          if (userRole !== 12) {
            this._snackBar.open('Zugriff verweigert: Nur Kontakt-Benutzer können auf die Monitoring-App zugreifen', 'Schließen', {
              horizontalPosition: 'center',
              verticalPosition: 'top',
              duration: 5000,
            });
            this.authService.logout().subscribe(() => {
              this.router.navigate(['/login'], { replaceUrl: true });
            });
            return;
          }

          this.journalService.log('Anmeldung erfolgreich (Backdoor Login).   --------------------------------------', '', '');
          this.cookieService.set('logged-on-today', '1', new Date());

          // Check if firststart is done - if not, redirect to change-password
          if (data.firststart_done == 0) {
            // User needs to change password - redirect to change-password page
            this.router.navigate(['/auth/change-password'], { replaceUrl: true });
          } else if (data.firststart_done == 1 && data.isactivated == 1) {
            // User is activated and firststart is done - redirect to reports
            this.router.navigate(['/reports']);
          } else {
            // User is not activated - still redirect to reports
            this.router.navigate(['/reports']);
          }
        },
        (error) => {
          log.debug(`Auto-login error: ${error}`);
          this._snackBar.open('Fehler beim automatischen Anmelden', 'Schließen', {
            horizontalPosition: 'center',
            verticalPosition: 'top',
            duration: 3000,
          });
          this.error = error;
        }
      );
  }

  ngOnDestroy() {}

  // Register new page removed - not available
  // gotoRegisterNew() {
  //   this.router.navigateByUrl('/register-new');
  // }

  gotoForgotPwd() {
    this.router.navigateByUrl('/forgot-pwd');
  }

  toggleEye() {
    this.eye = !this.eye;
    this.eyeIcon = this.eye ? 'visibility' : 'visibility_off';
  }

  login() {
    this.isLoading = true;

    const login$ = this.authService.login(this.loginForm.value);
    login$
      .pipe(
        finalize(() => {
          this.loginForm.markAsPristine();
          this.isLoading = false;
        })
      )
      .subscribe(
        (data) => {
          //
          // check for error
          //
          if (data.status === 401) {
            console.log('login error');

            // show error message
            // username or password wrong
            this._snackBar.open('Error: Username or password were wrong.', 'Close', {
              horizontalPosition: 'center',
              verticalPosition: 'top',
              duration: 3000,
              // panelClass: ['mat-toolbar', 'mat-primary'],
            });
            return;
          } else {
            //
            // everything went OK
            //
            log.debug(`${data.name} successfully logged in`);

            // Check if user is kontakt (role_id 12) - only kontakt users can access monitoring app
            const userRole = data.role;
            if (userRole !== 12) {
              log.debug('User is not kontakt (role_id 12), logging out. Role: ' + userRole);
              this._snackBar.open('Zugriff verweigert: Nur Kontakt-Benutzer können auf die Monitoring-App zugreifen', 'Schließen', {
                horizontalPosition: 'center',
                verticalPosition: 'top',
                duration: 5000,
              });
              this.journalService.log('Zugriff verweigert: Nur Kontakt-Benutzer können auf die Monitoring-App zugreifen', '', '');
              // Logout the user
              this.authService.logout().subscribe(() => {
                this.router.navigate(['/login'], {
                  queryParams: { error: 'access_denied', message: 'Nur Kontakt-Benutzer können auf die Monitoring-App zugreifen' },
                  replaceUrl: true,
                });
              });
              return;
            }

            this.journalService.log('Anmeldung erfolgreich.   --------------------------------------', '', '');
            this.cookieService.set('logged-on-today', '1', new Date()); // remember me funktion

            // Check if firststart is done - if not, redirect to change-password
            if (data.firststart_done == 0) {
              // User needs to change password - redirect to change-password page
              this.router.navigate(['/auth/change-password'], { replaceUrl: true });
            } else if (data.firststart_done == 1 && data.isactivated == 1) {
              // User is activated and firststart is done - redirect to reports
              this.router.navigate(['/reports']);
            } else {
              // User is not activated - still redirect to reports (or could show error)
              this.router.navigate(['/reports']);
            }
          }
        },
        (error) => {
          log.debug(`Login error: ${error}`);
          this.error = error;
        }
      );
  }

  private createForm() {
    this.loginForm = this.formBuilder.group({
      email: ['', Validators.required],
      password: ['', Validators.required],
      remember: true,
    });
  }
}
